import { useState } from "react";
import {
  Palette,
  Code2,
  TrendingUp,
  Wrench,
  ArrowRight,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const SERVICES = [
  {
    id: "web-design",
    icon: Palette,
    title: "Web Design",
    short:
      "Modern, user-focused layouts with clean UI and intuitive UX that make your brand stand out and convert.",
    long: "We create visually stunning and user-focused website designs that reflect your brand identity and instantly grab attention. Every layout is carefully planned to ensure smooth navigation, modern aesthetics, and a great user experience across all devices. Our goal is to make your website not just look good, but also feel intuitive and engaging for every visitor.",
    bullets: [
      "Brand-aligned visual identity",
      "Pixel-perfect responsive layouts",
      "Intuitive navigation & UX",
      "Conversion-focused design",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_stavan-dev-studio/artifacts/sxs2di34_Screenshot_20260614_183441_Gallery.jpg",
  },
  {
    id: "web-development",
    icon: Code2,
    title: "Web Development",
    short:
      "Fast, secure and scalable websites built with the latest technologies, engineered for stability and growth.",
    long: "We create powerful and modern websites that focus on speed, clarity, and user satisfaction. Each design is carefully structured to ensure smooth navigation and a clean visual experience across all screen sizes. Our development process emphasizes stability, efficiency, and long-term usability so your website always performs at its best. As your brand grows, we ensure your digital presence grows with it seamlessly.",
    bullets: [
      "Modern React & Next.js stack",
      "Clean, maintainable codebase",
      "Scalable architecture",
      "Cross-browser & device support",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_stavan-dev-studio/artifacts/s0gelbi5_4f6cb77548da3bf4a4e91e2d5d72049d.jpg",
  },
  {
    id: "seo-performance",
    icon: TrendingUp,
    title: "SEO & Performance",
    short:
      "Lightning-fast load times and on-page SEO so you rank higher and drive consistent organic growth.",
    long: "We optimize your website to rank higher on search engines and deliver lightning-fast performance. This includes improving loading speed, optimizing content structure, and applying SEO best practices. The result is better visibility, more traffic, and a stronger online presence that helps your business grow organically.",
    bullets: [
      "Core Web Vitals optimisation",
      "Technical & on-page SEO",
      "Image & asset compression",
      "Analytics & search console setup",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_stavan-dev-studio/artifacts/zxpc3oos_77429f8af3ed7f5da84c0f6a1a45f5f9.jpg",
  },
  {
    id: "maintenance-support",
    icon: Wrench,
    title: "Maintenance & Support",
    short:
      "Ongoing monitoring, security updates and bug fixes to keep your website running smoothly 24/7.",
    long: "We provide ongoing maintenance and technical support to keep your website updated, secure, and running without issues. From fixing bugs to updating content and monitoring performance, we handle everything so you can focus on your business while your website stays in perfect condition.",
    bullets: [
      "Continuous uptime monitoring",
      "Security patches & backups",
      "Content & SEO updates",
      "Performance health checks",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_stavan-dev-studio/artifacts/pq840ajx_64d0f829a5c2cf45e0ff307f5c879768.jpg",
  },
];

export const Services = () => {
  const [open, setOpen] = useState(null);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14">
          <div className="text-xs sm:text-sm tracking-[0.4em] text-[rgb(182,245,0)] font-mono-alt uppercase mb-4">
            What We Do
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
            Services That Help You{" "}
            <span className="text-[rgb(182,245,0)]">Grow</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.button
                key={s.id}
                data-testid={`service-card-${s.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                onClick={() => setOpen(s)}
                className="surface text-left rounded-2xl p-7 group"
              >
                <div className="w-12 h-12 rounded-xl bg-[rgb(182,245,0)] flex items-center justify-center mb-7 transition-transform group-hover:rotate-6">
                  <Icon className="w-6 h-6 text-black" strokeWidth={2.4} />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-3">
                  {s.title}
                </h3>
                <p className="text-sm text-white/55 leading-relaxed mb-6">
                  {s.short}
                </p>
                <span className="inline-flex items-center gap-2 text-sm text-[rgb(182,245,0)] font-medium">
                  Learn More{" "}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Detail modal: left info, right image */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setOpen(null)}
            data-testid="service-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.95, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 30 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-5xl w-full bg-black border border-[rgba(182,245,0,0.3)] rounded-3xl overflow-hidden lime-glow"
              data-testid={`service-modal-${open.id}`}
            >
              <button
                data-testid="service-modal-close"
                onClick={() => setOpen(null)}
                className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full bg-black border border-white/10 hover:border-[rgb(182,245,0)] flex items-center justify-center text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="grid lg:grid-cols-2">
                {/* LEFT INFO */}
                <div className="p-8 sm:p-12">
                  <div className="w-14 h-14 rounded-2xl bg-[rgb(182,245,0)] flex items-center justify-center mb-6">
                    <open.icon className="w-7 h-7 text-black" strokeWidth={2.4} />
                  </div>
                  <h3 className="font-display text-3xl sm:text-4xl font-bold text-white mb-5">
                    {open.title}
                  </h3>
                  <p className="text-white/65 leading-relaxed mb-6">
                    {open.long}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {open.bullets.map((b, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-sm text-white/85"
                      >
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[rgb(182,245,0)] shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid="service-modal-cta"
                    onClick={() => {
                      setOpen(null);
                      document
                        .getElementById("contact")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="btn-lime px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 text-sm"
                  >
                    Discuss Your Project <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {/* RIGHT IMAGE */}
                <div className="relative h-64 lg:h-auto min-h-[300px] bg-black">
                  <img
                    src={open.image}
                    alt={open.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent via-black/40 to-black" />
                  <div className="absolute inset-0 grid-bg opacity-30" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
